/**
 * useListingQueryState — generic hook that owns page/sort/filter/search
 * transitions for a listing page.
 *
 * Contract rules enforced here:
 *  - Narrowing changes (filters, search, scope changes) reset page.number to 1.
 *  - Sort change resets page.number to 1.
 *  - Page-size change resets page.number to 1.
 *  - Page-number change does NOT reset anything else.
 *  - State is returned as a plain value; selection and modal logic are NOT here.
 *
 * Generic parameter TFilters lets callers type their filter shapes without
 * this module knowing about inspection fields.
 */
import { useCallback, useReducer } from 'react';
// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function resetPage(page) {
    return Object.assign(Object.assign({}, page), { number: 1 });
}
function reducer(state, action) {
    switch (action.type) {
        case 'SET_PAGE_NUMBER':
            return Object.assign(Object.assign({}, state), { page: Object.assign(Object.assign({}, state.page), { number: action.pageNumber }) });
        case 'SET_PAGE_SIZE':
            return Object.assign(Object.assign({}, state), { page: { number: 1, size: action.pageSize } });
        case 'SET_SORT':
            return Object.assign(Object.assign({}, state), { sort: action.sort, page: resetPage(state.page) });
        case 'SET_FILTERS':
            return Object.assign(Object.assign({}, state), { filters: action.filters, page: resetPage(state.page) });
        case 'SET_SEARCH':
            return Object.assign(Object.assign({}, state), { search: action.search, page: resetPage(state.page) });
        case 'SET_SCOPE':
            return Object.assign(Object.assign({}, state), { scope: action.scope, page: resetPage(state.page) });
        case 'RESET':
            return action.state;
        default:
            return state;
    }
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useListingQueryState(initialState) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const setPageNumber = useCallback((pageNumber) => {
        dispatch({ type: 'SET_PAGE_NUMBER', pageNumber });
    }, []);
    const setPageSize = useCallback((pageSize) => {
        dispatch({ type: 'SET_PAGE_SIZE', pageSize });
    }, []);
    const setSort = useCallback((sort) => {
        dispatch({ type: 'SET_SORT', sort });
    }, []);
    const setFilters = useCallback((filters) => {
        dispatch({ type: 'SET_FILTERS', filters });
    }, []);
    const setSearch = useCallback((search) => {
        dispatch({ type: 'SET_SEARCH', search });
    }, []);
    const setScope = useCallback((scope) => {
        dispatch({ type: 'SET_SCOPE', scope });
    }, []);
    const reset = useCallback((nextState) => {
        dispatch({ type: 'RESET', state: nextState });
    }, []);
    return { state, setPageNumber, setPageSize, setSort, setFilters, setSearch, setScope, reset };
}
